import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon } from '@heroicons/react/24/outline';

/**
 * Public "For Clients" page — a brand-match of clinicstudios.ca/clients, moved
 * into the app so the marketing site can stay focused on Members (clinicians).
 *
 * Built to mirror the LIVE clinicstudios.ca/clients layout (full-bleed scrim
 * hero, alternating two-column sections, rich footer) per client direction,
 * with the palette per Nitika's suggested edits (Jun 2026):
 *   - Warm "lightened beige" base instead of white/sage  (clinic-beige)
 *   - Dark-green headings instead of black              (clinic-primary-dark)
 *   - Real photos in every slot                          (no placeholder boxes)
 *
 * Scope note: the beige/warm treatment is intentional for this PUBLIC marketing
 * page only — the authenticated app keeps the sage design system.
 *
 * Copy is verbatim from the source page. "Clinician Directory" CTAs point to
 * this app's /clinicians so visitors land in the live, bookable directory.
 */

const DIRECTORY_PATH = '/clinicians';
const MAIN_SITE = 'https://www.clinicstudios.ca';

// Nav is deliberately client-only. This page separates the CLIENT funnel from
// the MEMBER (clinician) world, so we drop the marketing site's Home/About/
// Members/Contact links — they'd pull prospective clients back toward the
// member-focused site. Header = the two client actions; the main site lives as
// one low-key escape hatch in the footer.

const SOCIALS = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/clinicstudioscanada/',
    path: 'M12 2.2c3.2 0 3.6 0 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s0 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58 0-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.16-.42-.36-1.06-.41-2.23C2.21 15.58 2.2 15.2 2.2 12s0-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.06-.36 2.23-.41C8.42 2.21 8.8 2.2 12 2.2zm0 1.62c-3.15 0-3.52.01-4.76.07-.9.04-1.39.2-1.71.32-.43.17-.74.37-1.06.69-.32.32-.52.63-.69 1.06-.12.32-.28.81-.32 1.71-.06 1.24-.07 1.61-.07 4.76s.01 3.52.07 4.76c.04.9.2 1.39.32 1.71.17.43.37.74.69 1.06.32.32.63.52 1.06.69.32.12.81.28 1.71.32 1.24.06 1.61.07 4.76.07s3.52-.01 4.76-.07c.9-.04 1.39-.2 1.71-.32.43-.17.74-.37 1.06-.69.32-.32.52-.63.69-1.06.12-.32.28-.81.32-1.71.06-1.24.07-1.61.07-4.76s-.01-3.52-.07-4.76c-.04-.9-.2-1.39-.32-1.71a2.85 2.85 0 00-.69-1.06 2.85 2.85 0 00-1.06-.69c-.32-.12-.81-.28-1.71-.32-1.24-.06-1.61-.07-4.76-.07zm0 2.76a5.42 5.42 0 110 10.84 5.42 5.42 0 010-10.84zm0 8.94a3.52 3.52 0 100-7.04 3.52 3.52 0 000 7.04zm6.9-9.15a1.27 1.27 0 11-2.54 0 1.27 1.27 0 012.54 0z',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/clinic-studios/posts/?feedView=all',
    path: 'M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28zM5.34 7.43a2.07 2.07 0 110-4.14 2.07 2.07 0 010 4.14zm1.78 13.02H3.56V9h3.56v11.45zM22.22 0H1.77C.79 0 0 .77 0 1.73v20.54C0 23.22.79 24 1.77 24h20.45c.98 0 1.78-.78 1.78-1.73V1.73C24 .77 23.2 0 22.22 0z',
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/people/Clinic-Studios/61583948475763/',
    path: 'M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.02 4.39 11.01 10.13 11.93v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.08 24 18.09 24 12.07z',
  },
];

// Slim gold accent rule under headings — one restrained brand accent.
const AccentRule: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`h-1 w-16 rounded-full bg-clinic-gold ${className}`} aria-hidden="true" />
);

// Section heading — dark green, display font, uppercase (matches live).
// Sized fluidly and nowrap-locked at lg+ so the hand-placed <br /> line breaks
// are EXACTLY the two lines Nitika specified at desktop widths — the earlier
// tracking-heavy 4xl version rewrapped to three lines on mid-size screens,
// which she flagged twice (Jun 10, Jul 15). Tracking traded down slightly so
// the type can run larger ("headers still look quite small") and still fit.
const SectionHeading: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <h2 className={`font-display text-[clamp(1.75rem,3vw,2.5rem)] font-bold uppercase tracking-[0.10em] text-clinic-primary-dark leading-snug lg:whitespace-nowrap ${className}`}>
    {children}
  </h2>
);


const ClientsPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-clinic-beige">
      <main>
        {/* ---------- Hero: full-bleed photo + sage scrim + centered copy ----------
            Height is a fixed cap, NOT viewport-proportional: the old min-h-[78vh]
            opened a large dead band between the nav and the headline on tall
            screens — the "large gap (depending on the screen size used)" Nitika
            red-boxed on Jul 15. */}
        <section className="relative min-h-[540px] lg:min-h-[600px] flex flex-col">
          {/* Background photo */}
          <img
            src="/assets/clients-hero.jpg"
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Sage wash + top/bottom darkening for legible white type */}
          <div className="absolute inset-0 bg-clinic-primary/55" />
          <div className="absolute inset-0 bg-gradient-to-b from-clinic-dark/35 via-transparent to-clinic-dark/45" />

          {/* Transparent top nav over the hero — client-only actions */}
          <header className="relative z-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-7 flex items-center justify-between">
              <Link to="/clients" className="flex items-center" aria-label="Clinic Studios — for clients">
                {/* Enlarged per Nitika Jun 5: "enlarge entire header (logo and
                    buttons at top)" — re-flagged Jun 10. */}
                <img
                  src="/logos/logo-primary.png"
                  alt="Clinic Studios"
                  className="h-14 sm:h-16 lg:h-[4.5rem] w-auto [filter:brightness(0)_invert(1)]"
                />
              </Link>
              <nav className="flex items-center gap-6 sm:gap-8 text-lg">
                <Link to={DIRECTORY_PATH} className="text-white/90 hover:text-clinic-gold transition-colors">Find a Clinician</Link>
                <Link
                  to="/login"
                  className="inline-flex items-center px-6 py-3 rounded-full border border-white/70 text-white hover:bg-white/10 transition-colors"
                >
                  Login
                </Link>
              </nav>
            </div>
          </header>

          {/* Hero content */}
          <div className="relative z-10 flex-1 flex items-center">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
              {/* Fluid size + controlled break: two lines at desktop instead of
                  the awkward three-line wrap in Nitika's Jul 15 screenshot. */}
              <h1 className="font-display text-[clamp(2.25rem,4.5vw,3.75rem)] font-bold text-white uppercase tracking-[0.08em] leading-[1.1] [text-shadow:0_2px_16px_rgba(0,0,0,0.35)]">
                A Place That Supports
                <br className="hidden sm:block" />
                {' '}Your Healing
              </h1>

              <div className="mt-8 inline-block rounded-2xl bg-clinic-dark/30 backdrop-blur-sm px-6 py-5 sm:px-8">
                <p className="text-base sm:text-lg text-white/95 leading-relaxed max-w-2xl">
                  Choosing a therapist is deeply personal and the setting where care takes place can shape
                  how comfortable and supported you feel. Clinic Studios was created with privacy, calm, and
                  meaningful connection in mind, offering purpose-built therapy spaces that help make each
                  visit feel welcoming and professional.
                </p>
                <Link
                  to={DIRECTORY_PATH}
                  className="group mt-6 inline-flex items-center px-6 py-2.5 rounded-full border border-white/80 text-white font-medium hover:bg-white/10 transition-colors"
                >
                  Clinician Directory
                  <ArrowRightIcon className="h-4 w-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Designed to support care: text left, photo right ---------- */}
        <section className="bg-clinic-beige">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div>
                <SectionHeading>Designed to<br />Support Care</SectionHeading>
                <AccentRule className="mt-5" />
                <p className="mt-6 text-lg text-clinic-dark/70 leading-relaxed">
                  Clinic Studios was built to support a better therapy experience for both Clinicians and the
                  people they serve. The spaces are designed to feel calm, welcoming, and professional, while
                  also protecting privacy and creating the conditions for meaningful connection. With thoughtful
                  design and intuitive systems behind the scenes, the focus stays on care.
                </p>
              </div>
              <div className="overflow-hidden rounded-card shadow-clinic">
                <img
                  src="/assets/clients-hallway.jpg"
                  alt="A calm, private hallway at Clinic Studios"
                  className="w-full object-cover aspect-[4/3] transition-transform duration-500 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Feel confident: photo left, text right ---------- */}
        <section className="bg-clinic-beige">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 lg:pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
              <div className="order-2 lg:order-1 overflow-hidden rounded-card shadow-clinic">
                {/* Nitika's supplied photo (Jun 4 "Suggested Image") — a real
                    session room shot, replacing the watermarked stock image. */}
                <img
                  src="/assets/clients-session.jpg"
                  alt="A therapy session in a private Clinic Studios room"
                  className="w-full object-cover aspect-[4/3] transition-transform duration-500 hover:scale-105"
                />
              </div>
              <div className="order-1 lg:order-2">
                <SectionHeading>Feel Confident in the<br />Care You Choose</SectionHeading>
                <AccentRule className="mt-5" />
                <p className="mt-6 text-lg text-clinic-dark/70 leading-relaxed">
                  Too often, mental health therapy takes place in spaces that feel improvised, shared, or
                  lacking in privacy. Clinic Studios was created differently. With dedicated therapy rooms, a
                  calm aesthetic, and an environment that reflects the seriousness and sensitivity of the work
                  taking place within it.
                </p>
                <p className="mt-4 text-lg text-clinic-dark/70 leading-relaxed">
                  Clinic Studios exists to support exceptional client care through thoughtfully designed,
                  reliable, professional workspaces built for privacy and meaningful connection. When you choose
                  a therapist who practices here, you are choosing a setting created to put you at ease.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ---------- Our Directory: warm band (lightened beige) ---------- */}
        <section className="bg-clinic-beige-deep">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 text-center">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-clinic-primary-dark">
              Our Directory
            </h2>
            <AccentRule className="mt-5 mx-auto" />
            <p className="mt-6 text-lg text-clinic-dark/70 leading-relaxed">
              Every therapist at Clinic Studios practises independently, bringing their own experience,
              specialties, and approach to care. The directory is here to help you browse, compare, and
              connect with the practitioner who feels like the best match for you.
            </p>
            <Link
              to={DIRECTORY_PATH}
              className="group mt-8 inline-flex items-center px-6 py-3 rounded-lg bg-clinic-primary text-white font-medium shadow-sm hover:bg-clinic-primary-dark hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
            >
              Clinician Directory
              <ArrowRightIcon className="h-4 w-4 ml-2 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </div>
        </section>
      </main>

      {/* ---------- Footer: dark green, logo + nav + contact + socials ---------- */}
      <footer className="bg-clinic-primary-dark text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 lg:gap-12">
            {/* Brand + client-only links */}
            <div>
              <img
                src="/logos/logo-icon.png"
                alt="Clinic Studios"
                className="h-12 w-auto mb-5 [filter:brightness(0)_invert(1)]"
              />
              <nav className="flex flex-col gap-2.5 text-sm">
                <Link to={DIRECTORY_PATH} className="text-white/90 hover:text-clinic-gold transition-colors">Find a Clinician</Link>
                <Link to="/login" className="text-white/90 hover:text-clinic-gold transition-colors">Login</Link>
                <a href={MAIN_SITE} className="text-white/60 hover:text-clinic-gold transition-colors">clinicstudios.ca ↗</a>
              </nav>
            </div>

            {/* Contact */}
            <div className="text-sm text-white/85 space-y-1">
              <p className="font-semibold text-white">Jameson Welbourn</p>
              <p><a href="mailto:jwelbourn@clinicstudios.ca" className="hover:text-clinic-gold transition-colors">jwelbourn@clinicstudios.ca</a></p>
              <p><a href="tel:+14034718617" className="hover:text-clinic-gold transition-colors">403.471.8617</a></p>
              <p className="pt-2">Suite 214<br />30 Springborough Blvd SW<br />Calgary, AB T3H 5M6</p>
            </div>

            {/* Socials */}
            <div className="col-span-2 sm:col-span-1 sm:justify-self-end">
              <p className="text-sm font-semibold text-white mb-3">Follow Us</p>
              <div className="flex gap-3">
                {SOCIALS.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.label}
                    className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-white/40 text-white/90 hover:text-clinic-gold hover:border-clinic-gold transition-colors"
                  >
                    <svg viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4" aria-hidden="true">
                      <path d={s.path} />
                    </svg>
                  </a>
                ))}
              </div>
            </div>
          </div>

          <p className="mt-10 pt-6 border-t border-white/15 text-xs text-white/60">
            &copy; {new Date().getFullYear()} Clinic Studios. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ClientsPage;
